// Video/Voice Call Page with 100ms Integration
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Video, Phone, Loader2, AlertCircle } from 'lucide-react';
import HMSVideoRoom from '../components/HMSVideoRoom';
import useHMS from '../hooks/useHMS';
import ChatSummary from '../components/ChatSummary';

export default function VideoCallPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [callEnded, setCallEnded] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [endedByOther, setEndedByOther] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(30); // Default 30 minutes
  
  // Track if join was already attempted
  const joinAttemptedRef = useRef(false);
  const sessionEndHandledRef = useRef(false);
  const pollIntervalRef = useRef(null);
  
  const {
    isConnected,
    hmsConfigured,
    createRoom,
    joinRoom,
    leaveRoom
  } = useHMS(sessionId);

  // Derive inCall from isConnected
  const inCall = isConnected;

  // Fetch session on mount
  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  // Poll for session status changes (to detect when other party ends call)
  useEffect(() => {
    if (!inCall || callEnded) return;
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if session was ended by the other party
        if (response.data.status === 'completed' && !sessionEndHandledRef.current) {
          sessionEndHandledRef.current = true;
          
          // Clear polling immediately
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          // Leave the 100ms room
          await leaveRoom();
          
          // Show notification
          toast.info('The other participant has ended the call', { duration: 3000 });
          
          setCallEnded(true);
          setEndedByOther(true);
          
          // For providers, show summary prompt instead of auto-redirect
          if (user?.role === 'care_provider') {
            setShowSummary(true);
          } else {
            // For seekers, auto-redirect after 2 seconds
            setTimeout(() => {
              navigate('/seeker-dashboard');
            }, 2000);
          }
        }
      } catch (err) {
        console.error('Session poll error:', err);
      }
    }, 2000); // Poll every 2 seconds for faster detection
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [inCall, callEnded, sessionId, leaveRoom, user, navigate]);

  // Track connection state changes for logging
  useEffect(() => {
    console.log('VideoCallPage: isConnected changed to', isConnected, '- inCall will be:', isConnected);
    if (isConnected) {
      setJoining(false);
    }
  }, [isConnected]);

  const fetchSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/sessions/${sessionId}`, { headers });
      setSession(response.data);
      console.log('Session loaded:', response.data);
      
      // Fetch session duration based on grade code or session type
      if (response.data.grade_code) {
        try {
          const gradesRes = await axios.get(`${API}/care-grades`);
          const grade = gradesRes.data.find(g => g.code === response.data.grade_code);
          if (grade?.duration_minutes) {
            setSessionDuration(grade.duration_minutes);
          }
        } catch (e) {
          console.log('Could not fetch grade duration, using default');
        }
      } else {
        // Default durations based on session type
        const defaultDurations = {
          'tier1': 30,
          'tier2': 45,
          'tier3': 60,
          'anonymous_voice': 15
        };
        const duration = defaultDurations[response.data.session_type] || 30;
        setSessionDuration(duration);
      }
      
      // Check if 100ms is configured
      const configRes = await axios.get(`${API}/hms/config`);
      if (!configRes.data.configured) {
        setError('Video calling is not configured. Please contact support.');
      }
    } catch (err) {
      console.error('Fetch session error:', err);
      setError('Session not found');
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = async () => {
    // Prevent double-joining
    if (joining || joinAttemptedRef.current || isConnected) {
      console.log('handleJoinCall: already joining or connected, skipping');
      return;
    }
    
    joinAttemptedRef.current = true;
    setJoining(true);
    setError(null);
    
    console.log('Starting join call process...');
    console.log('Session:', session);
    
    try {
      // First, create or get the 100ms room
      console.log('Creating/getting room...');
      const roomData = await createRoom(session?.channel || 'video');
      console.log('Room data:', roomData);
      
      if (!roomData?.room_id) {
        throw new Error('Failed to create room - no room_id returned');
      }
      
      // Then join the room
      console.log('Joining room:', roomData.room_id);
      const success = await joinRoom(roomData.room_id, {
        audioMuted: false,
        videoMuted: session?.channel === 'voice'
      });
      console.log('Join result:', success);
      
      if (!success) {
        throw new Error('Failed to join room - joinRoom returned false');
      }
      
      // Update session status to active if pending
      if (session?.status === 'pending' || session?.status === 'queued') {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/sessions/${sessionId}/status?status=active`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Session status updated to active');
      }
      
    } catch (err) {
      console.error('Join call error:', err);
      setError(err.message || 'Failed to join call');
      toast.error('Failed to join call: ' + (err.message || 'Unknown error'));
      setJoining(false);
      joinAttemptedRef.current = false;
    }
  };

  const handleLeaveCall = async () => {
    try {
      // Mark session as completed
      if (!sessionEndHandledRef.current) {
        sessionEndHandledRef.current = true;
        const token = localStorage.getItem('token');
        await axios.put(`${API}/sessions/${sessionId}/status?status=completed`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Also update appointment status if this session is linked to an appointment
        if (session?.appointment_id) {
          try {
            await axios.put(`${API}/appointments/${session.appointment_id}/status?status=completed`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Appointment status updated to completed');
          } catch (aptErr) {
            console.error('Error updating appointment status:', aptErr);
          }
        }
      }
    } catch (err) {
      console.error('Error updating session status:', err);
    }
    
    // Clear polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    // Leave 100ms room
    await leaveRoom();
    setCallEnded(true);
    joinAttemptedRef.current = false;
    
    // For providers, show summary prompt instead of auto-redirect
    if (user?.role === 'care_provider') {
      toast.success('Call ended. Please complete the session summary.');
      setShowSummary(true);
    } else {
      // For seekers, navigate to dashboard
      navigate('/seeker-dashboard');
      toast.success('Call ended');
    }
  };
  
  // Handle summary close - navigate to provider dashboard
  const handleSummaryClose = () => {
    setShowSummary(false);
    navigate('/provider-dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  // Show call ended screen when other party ends the call
  if (callEnded && endedByOther) {
    // For providers, show summary prompt
    if (user?.role === 'care_provider' && showSummary) {
      return (
        <div className="min-h-screen bg-gray-900">
          <ChatSummary
            sessionId={sessionId}
            onClose={handleSummaryClose}
            isOpen={true}
          />
        </div>
      );
    }
    
    // For seekers, show redirecting screen
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Phone className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            Call Ended
          </h2>
          <p className="text-gray-400 mb-4">
            The other participant has ended the call.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Redirecting to dashboard...
          </p>
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }
  
  // Show summary for provider when they end the call
  if (showSummary && user?.role === 'care_provider') {
    return (
      <div className="min-h-screen bg-gray-900">
        <ChatSummary
          sessionId={sessionId}
          onClose={handleSummaryClose}
          isOpen={true}
        />
      </div>
    );
  }

  if (error && !inCall) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
        <div className="card p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-[#2D3748] mb-2">
            Unable to Join Call
          </h2>
          <p className="text-[#718096] mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show video room when in call
  if (inCall) {
    return (
      <HMSVideoRoom 
        sessionId={sessionId}
        channel={session?.channel || 'video'}
        onLeave={handleLeaveCall}
        durationMinutes={sessionDuration}
      />
    );
  }

  // Pre-call lobby
  const isVoiceOnly = session?.channel === 'voice';
  const Icon = isVoiceOnly ? Phone : Video;
  
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 p-4">
        <div className="container mx-auto flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-lg hover:bg-gray-700 text-white"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">
              {isVoiceOnly ? 'Voice Call' : 'Video Call'}
            </h1>
            <p className="text-sm text-gray-400">
              {session?.grade_code || 'Session'} • {session?.status}
            </p>
            {user?.role === 'care_provider' && session?.presenting_concern && (
              <p
                className="text-xs text-gray-400/80 italic line-clamp-1 max-w-[60ch] mt-0.5"
                title={session.presenting_concern}
                data-testid="header-presenting-concern"
              >
                What brought them here:{' '}
                {session.presenting_concern.length > 100
                  ? `${session.presenting_concern.slice(0, 100)}…`
                  : session.presenting_concern}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Lobby */}
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
            <Icon size={48} className="text-primary" />
          </div>
          
          <h2 className="text-2xl font-heading font-semibold text-white mb-2">
            Ready to Join?
          </h2>
          
          <p className="text-gray-400 mb-6">
            {isVoiceOnly 
              ? 'Your microphone will be enabled when you join.'
              : 'Your camera and microphone will be enabled when you join.'}
          </p>

          {/* Session Info */}
          <div className="bg-gray-700 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Session Type</span>
              <span className="text-white capitalize">{session?.grade_code || session?.session_type}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Channel</span>
              <span className="text-white capitalize">{session?.channel}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status</span>
              <span className="text-green-400 capitalize">{session?.status}</span>
            </div>
          </div>

          {/* Mood Tags */}
          {session?.mood_tags && session.mood_tags.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-gray-400 mb-2">Concerns:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {session.mood_tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm capitalize">
                    {tag.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Join Button */}
          <button
            onClick={handleJoinCall}
            disabled={joining}
            data-testid="join-call-btn"
            className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {joining ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Icon size={20} />
                Join {isVoiceOnly ? 'Call' : 'Video Call'}
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 mt-4">
            By joining, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
